import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { AssessmentOption } from '../types';
import AssessmentScheduleFields, { type AssessmentScheduleValues } from './AssessmentScheduleFields';
import { getBrowserTimeZone } from '../utils/assessmentSchedule';

export type AddAssessmentPayload = {
  assessment_id: string;
  opens_at?: string;
  closes_at?: string;
  schedule_timezone?: string;
  include_pre_survey?: boolean;
  include_post_survey?: boolean;
};

interface AddAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddAssessmentPayload) => void;
  isSubmitting: boolean;
  assessmentOptions: AssessmentOption[];
}

function AddAssessmentDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  assessmentOptions,
}: AddAssessmentDialogProps) {
  const [assessmentId, setAssessmentId] = useState('');
  const [schedule, setSchedule] = useState<AssessmentScheduleValues>({ opensAt: '', closesAt: '', timeZone: '' });
  const [includePreSurvey, setIncludePreSurvey] = useState(true);
  const [includePostSurvey, setIncludePostSurvey] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setAssessmentId('');
    setSchedule({ opensAt: '', closesAt: '', timeZone: getBrowserTimeZone() });
    setIncludePreSurvey(true);
    setIncludePostSurvey(true);
    setLocalError(null);
  }, [open]);

  const handleSubmit = () => {
    if (!assessmentId) {
      setLocalError('Please select an assessment.');
      return;
    }

    if (schedule.opensAt && schedule.closesAt && schedule.closesAt <= schedule.opensAt) {
      setLocalError('Close time must be after open time.');
      return;
    }

    setLocalError(null);
    onSubmit({
      assessment_id: assessmentId,
      opens_at: schedule.opensAt || undefined,
      closes_at: schedule.closesAt || undefined,
      schedule_timezone: schedule.timeZone,
      include_pre_survey: includePreSurvey,
      include_post_survey: includePostSurvey,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Add Assessment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {assessmentOptions.length === 0 ? (
            <Alert severity="info">All active assessments are already linked to this class.</Alert>
          ) : (
            <>
              {localError && <Alert severity="error">{localError}</Alert>}
              <FormControl fullWidth required>
                <InputLabel id="assessment-select-label">Assessment</InputLabel>
                <Select
                  labelId="assessment-select-label"
                  value={assessmentId}
                  label="Assessment"
                  onChange={(event) => setAssessmentId(event.target.value)}
                >
                  {assessmentOptions.map((option) => (
                    <MenuItem key={option.assessment_id} value={option.assessment_id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <AssessmentScheduleFields value={schedule} onChange={setSchedule} />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={includePreSurvey}
                    onChange={(event) => setIncludePreSurvey(event.target.checked)}
                  />
                )}
                label="Include Pre-Assessment Survey"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={includePostSurvey}
                    onChange={(event) => setIncludePostSurvey(event.target.checked)}
                  />
                )}
                label="Include Post-Assessment Survey"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || assessmentOptions.length === 0}
        >
          {isSubmitting ? 'Adding...' : 'Add Assessment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddAssessmentDialog;
